import React, { PropsWithChildren } from 'react'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useWorkerRequirementsQuery } from 'queries/admin/credentials/useWorkerRequirementsQuery'
import { useCreateWorkerCredentialMutation } from 'queries/admin/credentials/useCreateWorkerCredentialMutation'

type AddWorkerCredentialFlowContextState = {
  state: {
    requirements: NonNullable<
      ReturnType<typeof useWorkerRequirementsQuery>['data']
    >['items']
    requirementsState: {
      isLoading: boolean
      isError: boolean
    }
    mutationState: {
      isPending: boolean
      isError: boolean
      errorMessage: string | undefined
    }
  }
  actions: {
    submit: ReturnType<typeof useCreateWorkerCredentialMutation>['mutate']
    reset: ReturnType<typeof useCreateWorkerCredentialMutation>['reset']
  }
}

const [Context, useAddWorkerCredentialFlowContextValue] =
  createContext<AddWorkerCredentialFlowContextState>({
    name: 'AddWorkerCredentialFlowContext',
  })

export const AddWorkerCredentialFlowProvider = ({
  children,
}: PropsWithChildren) => {
  const {
    data,
    isLoading: isRequirementsLoading,
    isError: isRequirementsError,
  } = useWorkerRequirementsQuery()

  const { mutate, reset, isPending, isError, error } =
    useCreateWorkerCredentialMutation()

  const value: AddWorkerCredentialFlowContextState = {
    state: {
      requirements: data?.items ?? [],
      requirementsState: {
        isLoading: isRequirementsLoading,
        isError: isRequirementsError,
      },
      mutationState: {
        isPending,
        isError,
        errorMessage: parseErrorMessage(error),
      },
    },
    actions: {
      submit: mutate,
      reset,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useAddWorkerCredentialFlowContextValue }
